import React from 'react';
import { X } from 'lucide-react';
import moment from 'moment';
import { useCreateRemediationPlan } from '../services/createRemediationPlan';

interface RemediationPreviewModalProps {
  show: boolean;
  onClose: () => void;
  selectedItems: any[];
  deviceData?: any;
}

const RemediationPreviewModal: React.FC<RemediationPreviewModalProps> = ({ show, onClose, selectedItems, deviceData }) => {
  // ✅ Hook must be called before any conditional return
  const { createRemediationPlan } = useCreateRemediationPlan();

  if (!show) return null;

  const handleCreatePlan = async () => {
    const remediationPlanData = {
      PlanName: `${deviceData?.deviceName} Remediation Plan - ${moment().format('YYYY-MM-DD HH:mm:ss')}`,
      IsEmergencyRemediationPlan: true,
      SendEmailOnCompletion: true,
      RemediationPlanStartTime: new Date().toISOString(),
      IsActive: true,
    };

    const remediationPlanPatchList = selectedItems;

    const targetDeviceIds = [deviceData?.agentId || ''];

    await createRemediationPlan({
      remediationPlanPatchList,
      targetDeviceIds,
      remediationPlanData,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
      <div className="bg-[#1c1c1c] text-white rounded-xl shadow-2xl p-6 w-[950px] max-h-[75vh] overflow-hidden transform transition-all duration-300 scale-100 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 pb-3 mb-4">
          <h2 className="text-xl font-semibold">Remediation Preview</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Summary */}
        <div className="mb-4 text-gray-300">
          You have selected <span className="font-semibold text-white">{selectedItems.length}</span> patches for remediation.
        </div>

        {/* Scrollable Table */}
        <div className="overflow-y-auto max-h-[55vh] border border-gray-700 rounded-lg scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          <table className="w-full text-sm text-gray-300">
            <thead className="sticky top-0 bg-[#252525] z-10">
              <tr>
                <th className="px-4 py-2 text-left">Package Name</th>
                <th className="px-4 py-2 text-left">Target Version</th>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-left">CVE Numbers</th>
                <th className="px-4 py-2 text-left">CVSS Scores</th>
                <th className="px-4 py-2 text-left">Release Date</th>
              </tr>
            </thead>

            <tbody>
              {selectedItems.map((item, idx) => (
                <tr key={idx} className="border-t border-gray-800 hover:bg-gray-800/60 transition-colors">
                  <td className="px-4 py-2">{item.PackageName || item.kbTitle || '-'}</td>
                  <td className="px-4 py-2">{item.TargetVersion || item.targetVersion || '-'}</td>
                  <td className="px-4 py-2">{item.category || item.Category || 'Update'}</td>
                  <td className="px-4 py-2">
                    {item.cveNumbers?.length ? item.cveNumbers.join(', ') : item.vulnerabilityDetail?.map((v) => v.cveId).join(', ') || '-'}
                  </td>
                  <td className="px-4 py-2">
                    {item.cvssScores?.length
                      ? item.cvssScores.map((s: number) => s.toFixed(2)).join(', ')
                      : item.vulnerabilityDetail?.length
                        ? item.vulnerabilityDetail.map((v: any) => (typeof v.score === 'number' ? v.score.toFixed(2) : v.score)).join(', ')
                        : '-'}
                  </td>
                  <td className="px-4 py-2">
                    {item.releaseDate || item.dateAvailable ? moment(item.releaseDate || item.dateAvailable).format('YYYY-MM-DD') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 space-x-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700 transition-all"
          >
            Cancel
          </button>
          <button onClick={handleCreatePlan} className="px-5 py-2 bg-red-700 hover:bg-red-800 rounded-full font-medium transition-all">
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemediationPreviewModal;
